import React, { useEffect, useState, useCallback } from "react";
import { Form, Table } from "react-bootstrap";
import Moment from "react-moment";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import {
  NoDataFoundTable,
  PageLoader,
} from "../../../Components/NoDataFoundTable/NoDataFoundTable";
import { editorProjectServices } from "../../../APIServices/Editor/editorProjectServices";

const TABS = [
  "information",
  "statements",
  "changeLogs",
  "softwareValidation",
  "procedureValidation",
];

const PackageDetails = ({ ids }) => {
  const [version, setVersion] = useState("");
  const [type, setType] = useState("information");
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState({
    information: {},
    statements: [],
    changeLogs: [],
    softwareValidation: [],
    procedureValidation: [],
  });
  const [changeLogMeta, setChangeLogMeta] = useState({});

  const getSinglePackageDetailsData = useCallback(async (tab, packageId, versionValue) => {
    try {
      setLoader(true);
      const res = await editorProjectServices.getSinglePackageDetailsData(
        tab,
        packageId,
        versionValue
      );

      if (!res?.data) return;

      switch (tab) {
        case "information":
          setData((prev) => ({ ...prev, information: res.data || {} }));
          break;
        case "statements":
          setData((prev) => ({
            ...prev,
            statements: res?.data?.statements?.statements || [],
          }));
          break;
        case "changeLogs":
          setChangeLogMeta(res.data || {});
          setData((prev) => ({
            ...prev,
            changeLogs: res.data?.changeLogs || [],
          }));
          break;
        case "softwareValidation":
          setData((prev) => ({
            ...prev,
            softwareValidation: res?.data?.softwareValidation || [],
          }));
          break;
        case "procedureValidation":
          setData((prev) => ({
            ...prev,
            procedureValidation: res?.data?.procedureValidation || [],
          }));
          break;
        default:
          break;
      }
    } catch (err) {
      console.error("Error fetching package details:", err);
    } finally {
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    if (ids?.packageId && (ids?.version || version)) {
      const currentVersion = version || ids.version;
      setVersion(currentVersion);
      getSinglePackageDetailsData(type, ids.packageId, currentVersion);
    }
  }, [ids, type, version, getSinglePackageDetailsData]);

  const handleVersionChange = (e) => {
    const selectedVersion = e.target.value;
    setVersion(selectedVersion);
    if (ids?.packageId) {
      getSinglePackageDetailsData(type, ids.packageId, selectedVersion);
    }
  };

  const versionOptions = Array.isArray(ids?.packageVersion)
    ? ids.packageVersion
    : [ids?.packageVersion];


  const renderInformation = () => {
    const info = data.information;
    if (!info || Object.keys(info).length === 0)
      return <NoDataFoundTable MSG="No information available!" />;

    return (
      <Table className="m-0 custom-table" bordered hover striped>
        <thead className="position-sticky top-0">
          <tr>
            <th style={{ width: 200 }}>Attribute</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Package Name</td>
            <td>{ids?.name || "NA"}</td>
          </tr>
          <tr>
            <td>Package Current Version</td>
            <td>v{ids?.version || "NA"}</td>
          </tr>
          <tr>
            <td>Package Type</td>
            <td className="text-capitalize">{ids?.packageType} Package</td>
          </tr>
          <tr>
            <td>Package Category</td>
            <td className="text-capitalize">{ids?.category || "NA"}</td>
          </tr>
          <tr>
            <td>Sort Description</td>
            <td>{ids?.description || "NA"}</td>
          </tr>
          <tr>
            <td>Developed By</td>
            <td>{ids?.developerName || "NA"}</td>
          </tr>
          <tr>
            <td>Package Status</td>
            <td className={`text-capitalize ${ids?.published === "processing" ? "text-warning-dark" : "text-success"}`}>{ids?.published || "NA"}</td>
          </tr>
          <tr>
            <td>Validated By</td>
            <td>{ids?.validatorName || "NA"}</td>
          </tr>
          <tr>
            <td>Review Status</td>
            <td className={`text-capitalize ${ids?.reviewStatus === "ongoing" ? "text-warning-dark" : "text-success"}`}>{ids?.reviewStatus || "NA"}</td>
          </tr>
          <tr>
            <td>Last Updated Date | Time</td>
            <td>
              <Moment format="DD MMM YYYY | HH:mm:ss">
                {ids?.updatedAt}
              </Moment>
            </td>
          </tr>
          <tr>
            <td>Published Date | Time</td>
            <td>
              <Moment format="DD MMM YYYY | HH:mm:ss">
                {ids?.createdAt}
              </Moment>
            </td>
          </tr>
        </tbody>
      </Table>
    );
  };

  const renderStatements = () => {
    const statements = data.statements;
    if (statements.length === 0)
      return <NoDataFoundTable MSG="Package statements not found!" />;

    return statements.map((item, i) => (
      <div key={i} className="overflow-auto mb-3 p-2">
        <Form.Label className="mb-1 fw-bold">
          {i + 1}. {item?.statement}
        </Form.Label>

        {item?.description && (
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 fw-bold">Description:</Form.Label>
            <div className="fontSize12">{item.description}</div>
          </Form.Group>
        )}

        {item?.syntax && (
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 fw-bold">Syntax:</Form.Label>
            <div className="fontSize12">{item.syntax};</div>
          </Form.Group>
        )}

        {item?.arguments?.length > 0 && (
          <>
            <Form.Label className="mb-1 fw-bold">Arguments:</Form.Label>
            <Table bordered hover size="sm" className="custom-table m-0">
              <tbody>
                {item.arguments.map((arg, k) => (
                  <tr key={k}>
                    <td className="fw-bold">{arg.attribute || "NA"}</td>
                    <td style={{ whiteSpace: "break-spaces" }}>{arg.value || "NA"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
        <hr />
      </div>
    ));
  };

  const renderChangeLogs = () => {
    const logs = data.changeLogs;
    if (logs.length === 0)
      return <NoDataFoundTable MSG="Changelogs not found!" />;

    return (
      <div className="p-3">
        <Form.Label className="mb-2">
          <b>
            – Released:{" "}
            <Moment format="DD MMM YYYY | HH:mm:ss" withTitle>
              {changeLogMeta?.createdAt}
            </Moment>
          </b>
        </Form.Label>
        <div className="log_change">
          {logs.map((item, idx) => (
            <pre key={idx}>- {item.type}: {item.changelog}</pre>
          ))}
        </div>
      </div>
    );
  };

  const renderSoftwareValidation = () => {
    const validations = data.softwareValidation;
    if (validations.length === 0)
      return <NoDataFoundTable MSG="Software validation data not found!" />;

    const softwareList = Object.keys(validations[0]?.responses || {});

    return (
      <Table hover striped className="m-0 custom-table">
        <thead className="position-sticky top-0 z-1">
          <tr>
            <th style={{ width: 85 }}>Example</th>
            <th>GAS</th>
            {softwareList.map((sw, i) => (
              <th key={i}>{sw}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {validations.map((row, i) => (
            <tr key={row.id || i}>
              <td><b>Example {i + 1}</b></td>
              <td><b>Output</b></td>
              {softwareList.map((sw, k) => {
                const response = row.responses[sw] || {};
                const { radio, file } = response;
                return (
                  <td key={k}>
                    <Form.Check
                      type="radio"
                      label="Match"
                      checked={radio === "match"}
                      disabled
                    />
                    <Form.Check
                      type="radio"
                      label="Not Match"
                      checked={radio === "notmatch"}
                      disabled
                    />
                    <div>
                      {file ? (
                        <a
                          href={file}
                          target="_blank"
                          rel="noreferrer"
                          className="hstack gap-2"
                        >
                          <i className="fa-solid fa-file-pdf text-dark CP fontSize12"></i>
                          <Form.Text>{`example-${i + 1}-${sw}-output`}</Form.Text>
                        </a>
                      ) : (
                        <Form.Text className="text-muted">
                          No file uploaded
                        </Form.Text>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  const renderProcedureValidation = () => {
    const list = data.procedureValidation;
    if (list.length === 0)
      return <NoDataFoundTable MSG="Procedure validation data not found!" />;

    return (
      <Table bordered hover striped className="m-0 custom-table">
        <thead>
          <tr>
            <th>Example</th>
            <th>Version</th>
            <th>Procedure</th>
          </tr>
        </thead>
        <tbody>
          {list.map((item, i) => (
            <tr key={i}>
              <td>{`Example ${i + 1}`}</td>
              <td>{`v1.0.0`}</td>
              <td>
                <SyntaxHighlighter language="sas" style={docco} className="m-0 bg-transparent">
                  {item.code}
                </SyntaxHighlighter>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  const renderContent = () => {
    if (loader) return <PageLoader />;
    switch (type) {
      case "information":
        return renderInformation();
      case "statements":
        return renderStatements();
      case "changeLogs":
        return renderChangeLogs();
      case "softwareValidation":
        return renderSoftwareValidation();
      case "procedureValidation":
        return renderProcedureValidation();
      default:
        return <NoDataFoundTable MSG="Select a tab to view details." />;
    }
  };

  return (
    <div className="overflow-hidden w-100">
      <div
        className="justify-content-between toolbar border-bottom hstack w-100 p-1"
        style={{ height: 35 }}
      >
        <div className="hstack gap-1">
          <h6 className="m-0 px-2">Packages</h6>
          <Form.Select onChange={handleVersionChange} value={version}>
            <option value="">Select Version</option>
            {versionOptions.map((v, i) => (
              <option key={i} value={v}>{v}</option>
            ))}
          </Form.Select>
        </div>

        <div className="hstack gap-1">
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`updateProfile ${type === tab ? "text-white" : ""}`}
              style={{
                backgroundColor: type === tab ? "#103c5e" : undefined,
              }}
              onClick={() => setType(tab)}
            >
              {tab.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())}
            </button>
          ))}
        </div>
      </div>

      <div style={{ height: "calc(100vh - 179px)", overflowY: "auto" }}>
        {renderContent()}
      </div>
    </div>
  );
};

export default PackageDetails;
